import { useVisitorPersonalization, getPersonalizedContent } from "@/hooks/useVisitorPersonalization";
import { Button } from "./ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const PersonalizedBanner = () => {
  const { profile, personalizedContent, isReturning } = useVisitorPersonalization();
  
  // Don't show personalized banner for unknown industry on first visit
  if (profile.industry === "unknown" && !isReturning) {
    return null;
  }
  
  // Show welcome back message for returning visitors
  const welcomeMessage = isReturning 
    ? `Welcome back! Based on your interests, we think you'll love these:` 
    : `Personalized for ${profile.industry.charAt(0).toUpperCase() + profile.industry.slice(1)} professionals:`;

  const industryRoutes: Record<string, string> = {
    restaurant: "/restaurants",
    hotel: "/hotels",
    healthcare: "/healthcare",
    warehouse: "/warehouses",
    education: "/education",
    retail: "/robots",
    unknown: "/robots"
  };

  return (
    <div className="relative overflow-hidden rounded-2xl glass-strong border border-primary/20 p-6 mb-8">
      {/* Background glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      
      <div className="relative z-10">
        {/* Personalization indicator */}
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-xs font-medium text-muted-foreground">
            {welcomeMessage}
          </span>
        </div>
        
        {/* Main content */}
        <h3 className="text-xl md:text-2xl font-bold font-orbitron mb-2">
          {personalizedContent.headline}
        </h3>
        <p className="text-muted-foreground mb-4 max-w-2xl">
          {personalizedContent.subheadline}
        </p>
        
        {/* Benefits */}
        <div className="flex flex-wrap gap-2 mb-4">
          {personalizedContent.benefits.map((benefit, index) => (
            <span 
              key={index}
              className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20"
            >
              ✓ {benefit}
            </span>
          ))}
        </div>
        
        {/* Featured robots */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <span className="text-sm font-medium text-muted-foreground">Recommended:</span>
          {personalizedContent.featuredRobots.map((robot, index) => (
            <span 
              key={index}
              className="text-sm font-semibold text-foreground"
            >
              {robot}{index < personalizedContent.featuredRobots.length - 1 ? " •" : ""}
            </span>
          ))}
        </div>
        
        {/* CTA */}
        <Button asChild className="gap-2">
          <Link to={industryRoutes[profile.industry]}>
            {personalizedContent.cta}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default PersonalizedBanner;
